/**
 * Author: Claude Code
 * Generation: Real-time platform statistics store
 * Version: v3.2.53
 * Modified: 2025-10-09
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface PlatformStats {
  totalAgents: number;        // Total AI agents created
  totalBattles: number;       // Total battles completed
  onlineViewers: string;      // Display format: "24/7" for always-on
  lastUpdated: Date;
}

interface StatsStore {
  stats: PlatformStats;
  updateStats: (stats: Partial<PlatformStats>) => void;
  setTotalAgents: (count: number) => void;
  setTotalBattles: (count: number) => void;
  incrementAgents: () => void;
  incrementBattles: () => void;
  resetStats: () => void;
}

const initialStats: PlatformStats = {
  totalAgents: 10,           // Default starting value
  totalBattles: 1000,        // Default starting value
  onlineViewers: "24/7",     // Always-on platform
  lastUpdated: new Date()
};

export const useStatsStore = create<StatsStore>()(
  immer((set) => ({
    stats: initialStats,

    updateStats: (newStats) => set((state) => {
      state.stats = {
        ...state.stats,
        ...newStats,
        lastUpdated: new Date()
      };
    }),

    setTotalAgents: (count) => set((state) => {
      state.stats.totalAgents = count;
      state.stats.lastUpdated = new Date();
    }),

    setTotalBattles: (count) => set((state) => {
      state.stats.totalBattles = count;
      state.stats.lastUpdated = new Date();
    }),

    incrementAgents: () => set((state) => {
      state.stats.totalAgents += 1;
      state.stats.lastUpdated = new Date();
    }),

    incrementBattles: () => set((state) => {
      state.stats.totalBattles += 1;
      state.stats.lastUpdated = new Date();
    }),

    resetStats: () => set((state) => {
      state.stats = { ...initialStats };
    })
  }))
);

// Selectors
export const usePlatformStats = () => useStatsStore(state => state.stats);
export const useStatsActions = () => useStatsStore(state => ({
  updateStats: state.updateStats,
  setTotalAgents: state.setTotalAgents,
  setTotalBattles: state.setTotalBattles,
  incrementAgents: state.incrementAgents,
  incrementBattles: state.incrementBattles
}));

// WebSocket integration hook (to be implemented)
export function useStatsWebSocket() {
  // TODO: Connect to backend WebSocket for real-time stats updates
  // Example WebSocket events:
  // - 'stats:agents' -> setTotalAgents
  // - 'stats:battles' -> setTotalBattles
  // - 'agent:created' -> incrementAgents
  // - 'battle:completed' -> incrementBattles
}
